import { usePopUpMessage } from '@hb/react'
import {
  objectToArray,
  PREGNANCY_CLINICAL_RATIONALES,
  PregnancyClinicalRationale,
  PregnancyClinicalRationales,
  reorder,
  SETTINGS,
  slugify,
  UpdateCallback,
  WithId,
} from '@hb/shared'
import { OnDragEndResponder } from '@hello-pangea/dnd'
import { deleteField, DocumentReference, writeBatch } from 'firebase/firestore'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { db } from '../../../../backend/db/db'
import { useDocument } from '../../../../backend/db/hooks'
import { useUpdateDoc } from '../../../../hooks'

export interface UsePregnancyClinicalRationales {
  data: PregnancyClinicalRationales | null
  loading: boolean
  ref: DocumentReference<PregnancyClinicalRationales> | null
  rationales: WithId<PregnancyClinicalRationale>[]
  handleUpdateCategoryName: (categoryId: string, name: string) => Promise<UpdateCallback>
  handleUpdateCategoryKeywords: (categoryId: string, keywords: string[]) => Promise<UpdateCallback>
  handleUpdateCategoryRationale: (categoryId: string, rationale: string) => Promise<UpdateCallback>
  handleDeleteCategory: (categoryId: string) => Promise<UpdateCallback>
  handleAddCategory: (category: PregnancyClinicalRationale) => Promise<UpdateCallback>
  handleDragEnd: OnDragEndResponder
}
export const usePregnancyClinicalRationales = (): UsePregnancyClinicalRationales => {
  const { data, loading, ref } = useDocument<PregnancyClinicalRationales>(
    SETTINGS,
    PREGNANCY_CLINICAL_RATIONALES,
  )
  const { showError } = usePopUpMessage()

  const update = useUpdateDoc('rationale')
  const serverRationales: WithId<PregnancyClinicalRationale>[] = useMemo(() => {
    if (!data) return []
    const { categories } = data
    return objectToArray(categories).sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
  }, [data])

  const [rationales, setRationales] = useState(serverRationales)
  const savePending = useRef(false)

  useEffect(() => {
    if (!savePending.current) {
      setRationales(serverRationales)
    }
  }, [serverRationales])

  const handleUpdateCategoryName = useCallback(
    async (categoryId: string, name: string) => {
      if (!ref) {
        return { error: 'No ref when updating category name' }
      }
      const newId = slugify(name)
      const batch = writeBatch(db)
      const updatedRationale = data?.categories?.[categoryId]
      if (!updatedRationale) {
        return { error: 'Category not found' }
      }
      // throw error if newId is already in use
      if (rationales.some(r => r.id === newId)) {
        showError(`Rationale ${name} already exists`)
        return { error: `Rationale ${name} already exists` }
      }
      batch.update(ref, `categories.${newId}`, updatedRationale)
      if (newId !== categoryId) {
        batch.update(ref, `categories.${categoryId}`, deleteField())
      }
      return batch
        .commit()
        .then(() => ({ success: 'Category name updated' }))
        .catch(err => ({ error: `Error updating category name: ${err.message}` }))
    },
    [rationales, showError, ref, data],
  )

  const handleUpdateCategoryKeywords = useCallback(
    async (categoryId: string, keywords: string[]) =>
      update(ref, `categories.${categoryId}.keywords`, keywords),
    [update, ref],
  )

  const handleUpdateCategoryRationale = useCallback(
    async (categoryId: string, rationale: string) =>
      update(ref, `categories.${categoryId}.rationale`, rationale),
    [update, ref],
  )

  const handleDeleteCategory = useCallback(
    async (categoryId: string) => update(ref, `categories.${categoryId}`, deleteField()),
    [update, ref],
  )
  const handleAddCategory = useCallback(
    async (category: PregnancyClinicalRationale) => {
      if (!ref) {
        return { error: 'No ref when adding category' }
      }
      const newId = slugify(category.category)
      if (!newId) {
        return { error: 'Category name is required' }
      }
      if (rationales.some(r => r.id === newId)) {
        return { error: `Category ${category.category} already exists` }
      }
      if (!category.keywords.length) {
        return { error: 'At least one keyword is required' }
      }
      if (!category.rationale) {
        return { error: 'Rationale is required' }
      }
      return update(ref, `categories.${newId}`, { ...category, rank: rationales.length })
    },
    [update, ref, rationales],
  )

  const handleDragEnd: OnDragEndResponder = useCallback(
    async result => {
      const { source, destination } = result
      if (!destination || source.index === destination.index) return
      if (!ref) return
      const reordered = reorder(rationales, result)
      setRationales(reordered)
      savePending.current = true
      const batch = writeBatch(db)
      reordered.forEach((item, idx) => {
        const originalRank = rationales.find(r => r.id === item.id)?.rank
        if (originalRank !== idx) {
          batch.update(ref, `categories.${item.id}.rank`, idx)
        }
      })
      await batch.commit()
      savePending.current = false
    },
    [rationales, ref],
  )

  return {
    data,
    loading,
    ref,
    rationales,
    handleUpdateCategoryName,
    handleUpdateCategoryKeywords,
    handleUpdateCategoryRationale,
    handleDeleteCategory,
    handleAddCategory,
    handleDragEnd,
  }
}
